// src/services/roleService.js
import { supabase } from './supabaseClient';

export const roleService = {
  /**
   * Get all roles with their permissions
   * @returns {Promise<Array<object>>}
   */
  async getAllRoles() {
    try {
      const { data: roles, error } = await supabase
        .from('roles')
        .select('id, name, description');

      if (error) throw error;

      // For each role, get its permissions
      const rolesWithPermissions = await Promise.all(
        roles.map(async (role) => {
          const { data: rolePerms, error: permError } = await supabase
            .from('role_permissions')
            .select('permissions(id, code, name, description, category)')
            .eq('role_id', role.id);

          if (permError) {
            console.error(`Error fetching permissions for role ${role.name}:`, permError);
            return { ...role, permissions: [] };
          }

          return {
            ...role,
            permissions: rolePerms.map(rp => rp.permissions),
          };
        })
      );

      return rolesWithPermissions;
    } catch (err) {
      console.error('Error fetching all roles:', err);
      return [];
    }
  },

  /**
   * Grant permission to role
   * @param {string} roleId - Role's UUID
   * @param {string} permissionCode - Permission code
   * @returns {Promise<void>}
   */
  async grantPermissionToRole(roleId, permissionCode) {
    try {
      // Get permission ID by code
      const { data: permission, error: permError } = await supabase
        .from('permissions')
        .select('id')
        .eq('code', permissionCode)
        .single();

      if (permError) throw new Error(`Permission ${permissionCode} not found`);

      // Insert into role_permissions
      const { error } = await supabase
        .from('role_permissions')
        .insert([
          {
            role_id: roleId,
            permission_id: permission.id,
          },
        ]);

      if (error && error.code !== '23505') {
        // 23505 is unique constraint violation (already exists), which is ok
        throw error;
      }
    } catch (err) {
      console.error('Error granting permission:', err);
      throw err;
    }
  },

  /**
   * Revoke permission from role
   * @param {string} roleId - Role's UUID
   * @param {string} permissionCode - Permission code
   * @returns {Promise<void>}
   */
  async revokePermissionFromRole(roleId, permissionCode) {
    try {
      // Get permission ID by code
      const { data: permission, error: permError } = await supabase
        .from('permissions')
        .select('id')
        .eq('code', permissionCode)
        .single();

      if (permError) throw new Error(`Permission ${permissionCode} not found`);

      // Delete from role_permissions
      const { error } = await supabase
        .from('role_permissions')
        .delete()
        .eq('role_id', roleId)
        .eq('permission_id', permission.id);

      if (error) throw error;
    } catch (err) {
      console.error('Error revoking permission:', err);
      throw err;
    }
  },

  /**
   * Get all available permissions
   * @returns {Promise<Array<object>>}
   */
  async getAllPermissions() {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('id, code, name, description, category')
        .order('category');

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching all permissions:', err);
      return [];
    }
  },

  /**
   * Create new permission (for future features)
   * @param {string} code - Permission code
   * @param {string} name - Human-readable name
   * @param {string} description - Description
   * @param {string} category - Category (Admin Panel, Frontend, etc.)
   * @returns {Promise<object>}
   */
  async createPermission(code, name, description, category) {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .insert([
          {
            code,
            name,
            description,
            category,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating permission:', err);
      throw err;
    }
  },
};
